/*
 * Bella Ballou: trust box on the product page (<bb-trust-box>), dispatch countdown row.
 *
 * All time maths runs in the browser because the page HTML is cached. "Now" is converted to the
 * wall-clock time of the configured timezone and held as a pseudo-UTC timestamp, so weekday, cutoff
 * and day arithmetic never depend on the visitor's own timezone or DST.
 *
 * The stock row (#Inventory-<section>) is swapped by Dawn's product-info.js on variant change;
 * a MutationObserver re-evaluates whether the dispatch row may be shown.
 *
 * Debug: ?bb_now=2026-09-19T16:00:00 fakes the start time (Copenhagen wall clock, then keeps ticking).
 * Only honoured in the theme editor (Shopify.designMode) or a theme preview (preview_theme_id in the URL,
 * or an unpublished theme, Shopify.theme.role !== 'main'). Ignored on the live theme.
 */
(function () {
  if (customElements.get('bb-trust-box')) return;

  const DAY = 86400000;
  const FALLBACK_TZ = 'Europe/Copenhagen';

  const toInt = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  // Wall-clock time in a timezone as a pseudo-UTC timestamp, or null if the timezone is invalid.
  function zonedWallClock(date, timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hourCycle: 'h23',
      }).formatToParts(date);
      const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
      return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    } catch (e) {
      return null;
    }
  }

  // "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM[:SS]" as a pseudo-UTC timestamp.
  function parseWall(value) {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(String(value).trim());
    if (!m) return null;
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
  }

  // One date or range (YYYY-MM-DD..YYYY-MM-DD) per line or comma; "# comment" is ignored.
  function parseClosedDates(text) {
    const closed = new Set();
    String(text || '')
      .split(/\r?\n/)
      .forEach((line) => {
        line
          .replace(/#.*/, '')
          .split(',')
          .forEach((token) => {
            const m = /^\s*(\d{4}-\d{2}-\d{2})\s*(?:\.\.\s*(\d{4}-\d{2}-\d{2}))?\s*$/.exec(token);
            if (!m) return;
            const start = parseWall(m[1]);
            const end = m[2] ? parseWall(m[2]) : start;
            if (start === null || end === null || end < start) return;
            for (let t = start, i = 0; t <= end && i < 400; t += DAY, i++) closed.add(t);
          });
      });
    return closed;
  }

  function debugStart() {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('bb_now');
      if (!raw) return null;
      // Shopify drops preview_theme_id from the URL after the first redirect (preview then lives in a
      // cookie), so an unpublished theme (role !== 'main') also counts as a preview.
      const shopify = window.Shopify || {};
      const allowed =
        shopify.designMode ||
        params.has('preview_theme_id') ||
        (shopify.theme && shopify.theme.role && shopify.theme.role !== 'main');
      return allowed ? parseWall(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function formatCountdown(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h} t ${m} min`;
    return m > 0 ? `${m} min ${s} sek` : `${s} sek`;
  }

  // Fill el with template text, replacing the placeholder with a node (or plain text).
  function fillTemplate(el, template, placeholder, replacement) {
    el.textContent = '';
    const pieces = template.split(placeholder);
    pieces.forEach((piece, index) => {
      if (piece) el.appendChild(document.createTextNode(piece));
      if (index < pieces.length - 1) {
        el.appendChild(typeof replacement === 'string' ? document.createTextNode(replacement) : replacement.cloneNode(true));
      }
    });
  }

  class BbTrustBox extends HTMLElement {
    connectedCallback() {
      this.dispatch = this.querySelector('[data-bb-dispatch]');
      this.dispatchText = this.querySelector('.bb-trust-box__dispatch-text');
      this.dispatchSr = this.querySelector('[data-bb-dispatch-sr]');
      this.stock = this.querySelector('.bb-trust-box__stock');
      if (!this.dispatch || !this.dispatchText) return;

      const ds = this.dataset;
      this.timeZone = ds.timezone || FALLBACK_TZ;
      if (zonedWallClock(new Date(), this.timeZone) === null) this.timeZone = FALLBACK_TZ;
      const hour = Math.min(23, Math.max(0, toInt(ds.cutoffHour, 15)));
      const minute = Math.min(59, Math.max(0, toInt(ds.cutoffMinute, 0)));
      this.cutoffMs = (hour * 60 + minute) * 60000;
      this.cutoffLabel = `kl. ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      this.closed = parseClosedDates(ds.closedDates);
      this.lang = document.documentElement.lang || 'da';

      this.debugWall = debugStart();
      this.debugStartedAt = Date.now();
      this.stateKey = null;

      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = window.setInterval(this.tick, 1000);

      if (this.stock && 'MutationObserver' in window) {
        this.observer = new MutationObserver(this.tick);
        this.observer.observe(this.stock, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      if (this.observer) this.observer.disconnect();
    }

    nowWall() {
      if (this.debugWall !== null) return this.debugWall + (Date.now() - this.debugStartedAt);
      const wall = zonedWallClock(new Date(), this.timeZone);
      if (wall !== null) return wall;
      const n = new Date();
      return Date.UTC(n.getFullYear(), n.getMonth(), n.getDate(), n.getHours(), n.getMinutes(), n.getSeconds());
    }

    isBusinessDay(dayStart) {
      const weekday = new Date(dayStart).getUTCDay();
      return weekday !== 0 && weekday !== 6 && !this.closed.has(dayStart);
    }

    canShip() {
      if (!this.stock || this.stock.classList.contains('hidden')) return false;
      const marker = this.stock.querySelector('[data-bb-stock]');
      if (!marker) return false;
      const state = marker.getAttribute('data-bb-stock');
      return (state === 'in' || state === 'low') && marker.getAttribute('data-bb-ship') !== 'false';
    }

    dayLabel(today, next) {
      const ds = this.dataset;
      const days = Math.round((next - today) / DAY);
      const date = new Date(next);
      if (days === 1) return ds.labelTomorrow || 'i morgen';
      if (days < 7) {
        const weekday = new Intl.DateTimeFormat(this.lang, { weekday: 'long', timeZone: 'UTC' }).format(date);
        return `${ds.labelWeekdayPrefix || 'på'} ${weekday}`;
      }
      const dayMonth = new Intl.DateTimeFormat(this.lang, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date);
      return `${ds.labelDatePrefix || 'den'} ${dayMonth}`;
    }

    tick() {
      if (!this.canShip()) {
        this.dispatch.hidden = true;
        this.stateKey = null;
        return;
      }

      const ds = this.dataset;
      const now = this.nowWall();
      const today = Math.floor(now / DAY) * DAY;
      const cutoff = today + this.cutoffMs;

      if (this.isBusinessDay(today) && now < cutoff) {
        const template = ds.textBeforeCutoff || 'Pakken sendes inden for 1-3 hverdage';
        if (this.stateKey !== 'before') {
          this.stateKey = 'before';
          const strong = document.createElement('strong');
          strong.className = 'bb-trust-box__countdown';
          strong.setAttribute('aria-live', 'off');
          fillTemplate(this.dispatchText, template, '[countdown]', strong);
          if (this.dispatchSr) fillTemplate(this.dispatchSr, template, '[countdown]', this.cutoffLabel);
        }
        const countdown = formatCountdown(cutoff - now);
        this.dispatchText.querySelectorAll('.bb-trust-box__countdown').forEach((el) => {
          if (el.textContent !== countdown) el.textContent = countdown;
        });
      } else {
        let next = today + DAY;
        for (let i = 0; i < 60 && !this.isBusinessDay(next); i++) next += DAY;
        const key = `after-${today}-${next}`;
        if (this.stateKey !== key) {
          this.stateKey = key;
          const template = ds.textAfterCutoff || 'Pakken sendes inden for 1-3 hverdage';
          const label = this.dayLabel(today, next);
          fillTemplate(this.dispatchText, template, '[day]', label);
          if (this.dispatchSr) fillTemplate(this.dispatchSr, template, '[day]', label);
        }
      }

      this.dispatch.hidden = false;
    }
  }

  customElements.define('bb-trust-box', BbTrustBox);
})();
